'use client';

import { c as _c } from "react/compiler-runtime";
import { Slider } from '@common/components';
import { SvgShareLink } from '@common/icon';
import { cn } from '@core/utils';
import { useState } from 'react';
import CountUp from 'react-countup';
import { CustomLink } from '@/app/_components/CustomLink';
import { ROUTES, SEARCH_PARAMS } from '@/constants';
import { useClipboard } from '@/hooks/useClipboard';
import { CONTENT_CATEGORY, GTM_EVENT_TYPE, trackGa4Event, trackGtmEvent } from '@/utils/analytics/gtm';
import type { IncomeBand, Recommendation, SavingsScenario } from '../_utils/youthSavingsCalc';
import { benefitPerHundred, calculateFutureSavings, calculateLeapAccount, resolveIncomeBand, resolveRecommendation } from '../_utils/youthSavingsCalc';
interface ShareContext {
  title: string;
  contentId: string;
}
interface YouthSavingsCalculatorProps {
  shareContext?: ShareContext;
}
const formatManwon = (value: number): string => `${Math.round(value).toLocaleString('ko-KR')}만원`;
const formatPeriod = (months: number): string => {
  const years = Math.floor(months / 12);
  const restMonths = months % 12;
  if (years > 0 && restMonths > 0) {
    return `${years}년 ${restMonths}개월`;
  }
  if (years > 0) {
    return `${years}년`;
  }
  return `${restMonths}개월`;
};
const BAND_LABEL: Record<IncomeBand, string> = {
  preferential: '우대형 +12%',
  general: '일반형 +6%',
  taxFree: '비과세형',
  excess: '소득 초과 · 가입 불가'
};
export const YouthSavingsCalculator = t0 => {
  const $ = _c(61);
  const {
    shareContext
  } = t0;
  const [monthlyPayment, setMonthlyPayment] = useState(50);
  const [remainingMonths, setRemainingMonths] = useState(36);
  const [annualSalary, setAnnualSalary] = useState(3000);
  let band;
  let future;
  let leap;
  let recommendation;
  let t1;
  if ($[0] !== annualSalary || $[1] !== monthlyPayment || $[2] !== remainingMonths) {
    band = resolveIncomeBand(annualSalary);
    leap = calculateLeapAccount(monthlyPayment, remainingMonths, band);
    future = calculateFutureSavings(monthlyPayment, band);
    recommendation = resolveRecommendation({
      remainingMonths,
      monthlyPayment,
      leap,
      future
    });
    t1 = buildRecommendationCopy(recommendation, {
      remainingMonths,
      leapPerHundred: benefitPerHundred(leap),
      futurePerHundred: future ? benefitPerHundred(future) : 0
    });
    $[0] = annualSalary;
    $[1] = monthlyPayment;
    $[2] = remainingMonths;
    $[3] = band;
    $[4] = future;
    $[5] = leap;
    $[6] = recommendation;
    $[7] = t1;
  } else {
    band = $[3];
    future = $[4];
    leap = $[5];
    recommendation = $[6];
    t1 = $[7];
  }
  const copy = t1;
  const winnerScenario = recommendation.winner === "future" ? future : leap;
  const winnerBenefit = winnerScenario?.benefit ?? leap.benefit;
  let t2;
  if ($[8] !== future || $[9] !== leap || $[10] !== recommendation || $[11] !== shareContext) {
    t2 = shareContext ? <ShareSection recommendation={recommendation} leap={leap} future={future} shareContext={shareContext} /> : null;
    $[8] = future;
    $[9] = leap;
    $[10] = recommendation;
    $[11] = shareContext;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== copy.title) {
    t3 = <span className="font-600 text-Gray-700 typo-body4">{copy.title}</span>;
    $[13] = copy.title;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== winnerBenefit) {
    t4 = <strong className="font-700 tabular-nums text-B-500 typo-h3">+<CountUp end={winnerBenefit} duration={0.4} preserveValue={true} formattingFn={formatManwon} /></strong>;
    $[15] = winnerBenefit;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== copy.body) {
    t5 = <p role="status" className="flex min-h-40 items-center break-keep font-400 text-Gray-600 typo-caption1">{copy.body}</p>;
    $[17] = copy.body;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== t3 || $[20] !== t4 || $[21] !== t5) {
    t6 = <div aria-live="polite" className="flex flex-col items-center gap-2 text-center">{t3}{t4}{t5}</div>;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const t7 = leap.monthlyPayment;
  let t8;
  if ($[23] !== remainingMonths) {
    t8 = formatPeriod(remainingMonths);
    $[23] = remainingMonths;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const t9 = `월 ${t7}만 · ${t8}`;
  const t10 = recommendation.winner === "leap";
  let t11;
  if ($[25] !== leap || $[26] !== t10 || $[27] !== t9) {
    t11 = <ScenarioPanel name={"\uCCAD\uB144\uB3C4\uC57D\uACC4\uC88C"} tag={"\uADF8\uB300\uB85C \uC720\uC9C0"} periodText={t9} scenario={leap} isWinner={t10} />;
    $[25] = leap;
    $[26] = t10;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const t12 = future ? `월 ${future.monthlyPayment}만 · 3년` : "";
  const t13 = recommendation.winner === "future";
  let t14;
  if ($[29] !== future || $[30] !== t12 || $[31] !== t13) {
    t14 = <ScenarioPanel name={"\uCCAD\uB144\uBBF8\uB798\uC801\uAE08"} tag={"\uAC08\uC544\uD0C0\uAE30"} periodText={t12} scenario={future} isWinner={t13} unavailableText={"\uC18C\uB4DD\uC774 \uB192\uC544 \uAC00\uC785 \uB300\uC0C1\uC774 \uC544\uB2C8\uC5D0\uC694"} />;
    $[29] = future;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t11 || $[34] !== t14) {
    t15 = <div className="grid grid-cols-2 gap-12">{t11}{t14}</div>;
    $[33] = t11;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const t16 = `${monthlyPayment}만원`;
  let t17;
  if ($[36] !== monthlyPayment || $[37] !== t16) {
    t17 = <SliderField label={"\uB9E4\uB2EC \uB123\uC744 \uAE08\uC561"} valueText={t16} min={10} max={70} step={5} value={monthlyPayment} onValueChange={setMonthlyPayment} />;
    $[36] = monthlyPayment;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== remainingMonths) {
    t18 = formatPeriod(remainingMonths);
    $[39] = remainingMonths;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== remainingMonths || $[42] !== t18) {
    t19 = <SliderField label={"\uB0A8\uC740 \uAE30\uAC04"} valueText={t18} min={6} max={60} step={1} value={remainingMonths} onValueChange={setRemainingMonths} />;
    $[41] = remainingMonths;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== annualSalary) {
    t20 = annualSalary >= 8000 ? "8,000\uB9CC\uC6D0+" : `${annualSalary.toLocaleString("ko-KR")}만원`;
    $[44] = annualSalary;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  const t21 = BAND_LABEL[band];
  const t22 = band === "excess" ? "warning" : "muted";
  let t23;
  if ($[46] !== annualSalary || $[47] !== t20 || $[48] !== t21 || $[49] !== t22) {
    t23 = <SliderField label={"\uC791\uB144 \uC5F0\uC18C\uB4DD (\uC138\uC804)"} valueText={t20} hint={t21} hintTone={t22} min={0} max={8000} step={100} value={annualSalary} onValueChange={setAnnualSalary} />;
    $[46] = annualSalary;
    $[47] = t20;
    $[48] = t21;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t17 || $[52] !== t19 || $[53] !== t23) {
    t24 = <div className="flex flex-col gap-12 border-t border-Gray-100 pt-16">{t17}{t19}{t23}</div>;
    $[51] = t17;
    $[52] = t19;
    $[53] = t23;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className="mt-auto flex flex-col items-center gap-8 pt-16"><CustomLink href={ROUTES.RECOMMEND_POLICY_LIST.pathname} className="font-600 text-Gray-700 underline typo-caption1">내게 맞는 정책 더 보기</CustomLink><p className="break-keep text-center font-400 text-Gray-400 typo-caption2">참고용 시뮬레이션이에요. 실제 금액은 가입 전 공식 안내를 확인하세요.</p></div>;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t15 || $[57] !== t2 || $[58] !== t24 || $[59] !== t6) {
    t26 = <div className="relative flex flex-col gap-16 mobile-tablet:flex-1">{t2}{t6}{t15}{t24}{t25}</div>;
    $[56] = t15;
    $[57] = t2;
    $[58] = t24;
    $[59] = t6;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  return t26;
};
interface ShareSectionProps {
  recommendation: Recommendation;
  leap: SavingsScenario;
  future: SavingsScenario | null;
  shareContext: ShareContext;
}
const ShareSection = t0 => {
  const $ = _c(12);
  const {
    recommendation,
    leap,
    future,
    shareContext
  } = t0;
  const copyToClipboard = useClipboard();
  let t1;
  if ($[0] !== future || $[1] !== leap || $[2] !== recommendation.winner) {
    t1 = recommendation.winner === "future" && future ? `청년미래적금 갈아타기 vs 도약계좌 유지, 내 조건으론 갈아타기가 +${formatManwon(future.benefit)} 유리했어요. 조건만 넣으면 1분 만에 계산돼요.` : `청년미래적금 갈아타기 vs 도약계좌 유지, 내 조건으론 도약계좌 유지가 +${formatManwon(leap.benefit)} 유리했어요. 조건만 넣으면 1분 만에 계산돼요.`;
    $[0] = future;
    $[1] = leap;
    $[2] = recommendation.winner;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const shareSummary = t1;
  let t2;
  if ($[4] !== copyToClipboard || $[5] !== shareContext.contentId || $[6] !== shareContext.title || $[7] !== shareSummary) {
    t2 = async () => {
      const shareUrl = `https://www.welfarehello.com/api/redirect/019ef327-5b79-794b-a38a-99b594026806?${SEARCH_PARAMS.ENTER_TYPE}=share`;
      const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
      const eventParams = {
        method: canNativeShare ? "\uACF5\uC720\uD558\uAE30" : "\uB9C1\uD06C\uBCF5\uC0AC",
        content_name: shareContext.title,
        content_category: CONTENT_CATEGORY.POLICY_NEWS,
        content_id: shareContext.contentId
      };
      trackGtmEvent(GTM_EVENT_TYPE.SHARE, eventParams);
      trackGa4Event("share", eventParams);
      if (canNativeShare) {
        try {
          await navigator.share({
            title: shareContext.title,
            text: shareSummary,
            url: shareUrl
          });
        } catch {}
        return;
      }
      copyToClipboard({
        value: `${shareSummary}\n${shareUrl}`,
        message: "\uB9C1\uD06C\uB97C \uBCF5\uC0AC\uD588\uC5B4\uC694. \uCE5C\uAD6C\uC5D0\uAC8C \uBD99\uC5EC\uB123\uC5B4 \uBCF4\uC138\uC694."
      });
    };
    $[4] = copyToClipboard;
    $[5] = shareContext.contentId;
    $[6] = shareContext.title;
    $[7] = shareSummary;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const handleShare = t2;
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SvgShareLink className="size-20" />;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== handleShare) {
    t4 = <button onClick={handleShare} aria-label={"\uACB0\uACFC \uACF5\uC720\uD558\uAE30"} className="absolute right-0 top-0 z-10 flex size-40 items-center justify-center rounded-medium text-Gray-600 transition-colors hover:bg-Gray-50 hover:text-B-500">{t3}</button>;
    $[10] = handleShare;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
};
interface SliderFieldProps {
  label: string;
  valueText: string;
  hint?: string;
  hintTone?: 'muted' | 'warning';
  min: number;
  max: number;
  step: number;
  value: number;
  onValueChange: (next: number) => void;
}
const SliderField = t0 => {
  const $ = _c(26);
  const {
    label,
    valueText,
    hint,
    hintTone: t1,
    min,
    max,
    step,
    value,
    onValueChange
  } = t0;
  const hintTone = t1 === undefined ? "muted" : t1;
  let t2;
  if ($[0] !== hint || $[1] !== hintTone) {
    t2 = hint ? <span className={cn("ml-6 font-600", hintTone === "warning" ? "text-R-600" : "text-Gray-400")}>{hint}</span> : null;
    $[0] = hint;
    $[1] = hintTone;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== label || $[4] !== t2) {
    t3 = <span className="font-500 text-Gray-600 typo-caption1">{label}{t2}</span>;
    $[3] = label;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== valueText) {
    t4 = <span className="whitespace-nowrap font-700 tabular-nums text-Gray-900 typo-body3">{valueText}</span>;
    $[6] = valueText;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <div className="flex items-baseline justify-between gap-12">{t3}{t4}</div>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== value) {
    t6 = [value];
    $[11] = value;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== onValueChange) {
    t7 = next => onValueChange(next[0]);
    $[13] = onValueChange;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== label || $[16] !== max || $[17] !== min || $[18] !== step || $[19] !== t6 || $[20] !== t7 || $[21] !== valueText) {
    t8 = <Slider aria-label={label} aria-valuetext={valueText} min={min} max={max} step={step} value={t6} onValueChange={t7} />;
    $[15] = label;
    $[16] = max;
    $[17] = min;
    $[18] = step;
    $[19] = t6;
    $[20] = t7;
    $[21] = valueText;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t5 || $[24] !== t8) {
    t9 = <div className="flex flex-col gap-8">{t5}{t8}</div>;
    $[23] = t5;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
};
interface ScenarioPanelProps {
  name: string;
  tag: string;
  periodText: string;
  scenario: SavingsScenario | null;
  isWinner: boolean;
  unavailableText?: string;
}
const ScenarioPanel = t0 => {
  const $ = _c(19);
  const {
    name,
    tag,
    periodText,
    scenario,
    isWinner,
    unavailableText
  } = t0;
  const t1 = isWinner ? "border-B-300 bg-B-25 shadow-sm ring-2 ring-B-200" : "border-Gray-100 bg-Gray-50 shadow-none ring-2 ring-transparent";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex flex-col gap-4 rounded-medium border-1 p-12 transition-all duration-300", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== name) {
    t3 = <span className="font-700 text-Gray-900 typo-body3">{name}</span>;
    $[2] = name;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = isWinner ? "bg-B-500 text-Gray-white" : "bg-Gray-100 text-Gray-500";
  let t5;
  if ($[4] !== t4) {
    t5 = cn("inline-flex w-fit rounded-pill px-8 py-2 font-600 transition-colors duration-300 typo-caption2", t4);
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5 || $[7] !== tag) {
    t6 = <span className={t5}>{tag}</span>;
    $[6] = t5;
    $[7] = tag;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== isWinner || $[10] !== periodText || $[11] !== scenario || $[12] !== unavailableText) {
    t7 = scenario ? <><strong className={cn("mt-4 font-700 tabular-nums", isWinner ? "text-B-500 typo-h6" : "text-Gray-500 typo-h7")}><CountUp end={scenario.total} duration={0.4} preserveValue={true} formattingFn={formatManwon} /></strong><span className="font-400 text-Gray-500 typo-caption2">{periodText}</span><div aria-hidden={true} className="mt-4 flex h-10 overflow-hidden rounded-pill bg-Gray-100"><div className="bg-Gray-300 transition-[width] duration-300 ease-out" style={{
          width: `${scenario.principal / scenario.total * 100}%`
        }} /><div className={cn("transition-all duration-300 ease-out", isWinner ? "bg-B-400" : "bg-Gray-400")} style={{
          width: `${scenario.benefit / scenario.total * 100}%`
        }} /></div><p className="break-keep font-400 tabular-nums text-Gray-600 typo-caption2">원금 {formatManwon(scenario.principal)}</p><p className="break-keep font-400 tabular-nums text-Gray-500 typo-caption2">정부 {formatManwon(scenario.government)} · 이자{" "}{formatManwon(scenario.interest)}</p><p className="mt-2 break-keep font-600 tabular-nums typo-caption1">혜택{" "}<b className={cn("font-700", isWinner ? "text-B-500" : "text-Gray-700")}>+<CountUp end={scenario.benefit} duration={0.4} preserveValue={true} formattingFn={formatManwon} /></b></p></> : <p className="mt-4 break-keep font-600 text-Gray-500 typo-body4">{unavailableText}</p>;
    $[9] = isWinner;
    $[10] = periodText;
    $[11] = scenario;
    $[12] = unavailableText;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t2 || $[15] !== t3 || $[16] !== t6 || $[17] !== t7) {
    t8 = <div className={t2}>{t3}{t6}{t7}</div>;
    $[14] = t2;
    $[15] = t3;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
};
const buildRecommendationCopy = (recommendation: Recommendation, values: {
  remainingMonths: number;
  leapPerHundred: number;
  futurePerHundred: number;
}): {
  title: string;
  body: string;
} => {
  switch (recommendation.reason) {
    case 'incomeExcess':
      return {
        title: '청년도약계좌를 유지하세요',
        body: '소득이 높아 청년미래적금은 가입 대상이 아니에요. 지금 계좌를 그대로 두는 게 좋아요.'
      };
    case 'nearMaturity':
      return {
        title: '청년도약계좌를 유지하세요',
        body: `만기가 ${formatPeriod(values.remainingMonths)} 정도밖에 안 남았어요. 지금 갈아타면 3년을 다시 시작해서 손해예요.`
      };
    case 'highCapacity':
      return {
        title: '청년도약계좌를 유지하세요',
        body: '매달 60만원 넘게 넣을 수 있다면, 한도가 더 큰 도약계좌(월 70만·5년)가 최종 목돈에서 앞서요.'
      };
    case 'futureHigherReturn':
      return {
        title: '청년미래적금으로 갈아타세요',
        body: `100만원당 약 ${values.futurePerHundred}만원이 붙어요. 도약계좌(약 ${values.leapPerHundred}만원)보다 많아 갈아타는 게 이득이에요.`
      };
    case 'leapHigherReturn':
      return {
        title: '청년도약계좌를 유지하세요',
        body: `100만원당 약 ${values.leapPerHundred}만원이 붙어요. 미래적금(약 ${values.futurePerHundred}만원)보다 많아 그대로 두는 게 이득이에요.`
      };
  }
};